import { t } from "@lingui/macro";
import React, { useState } from "react";

import {
  VStack,
  Text,
  HStack,
} from "@whoosh/design-system/src/components/primitive";
import { useFormatClubCurrency } from "@whoosh/features/src/ClubCurrency";

import { MultiSelectCheckbox } from "../../controls/DropdownSelect/MultiSelectCheckbox";
import { SubArrowIcon } from "../../icons_v2/general";
import { Box } from "../../primitive";
import { Avatar } from "../Avatar";
import { UnknownGuestChargeRow } from "./UnknownGuestChargeRow";

export type PayerType = "MEMBER" | "GUEST" | "New Guest" | "CUSTOMER";

export type EventChargeRowData = {
  firstName: string | null;
  lastName: string | null;
  id: string;
  profileImageId?: string;
  price: number;
  detail?: string;
  type: PayerType;
  isCurrentUser?: boolean;
  payingMemberId?: string;
  displayName?: string | null;
  displayDescription?: string | null;
  unknownRegistrantDisplayName?: string | null;
};

export type EventChargePayerRowDataPayForOption = {
  value: string;
  type: "unknownGuest" | "row";
  label: string;
  isDisabled?: boolean;
};

export type EventChargePayerRowData = {
  payer: EventChargeRowData;
  additionalChargeRows: EventChargeRowData[];
  shouldShowPayForDropdown?: boolean;
  payForOptions?: EventChargePayerRowDataPayForOption[];
  displayName?: string | null;
};

export type ChargePayerRowProps = EventChargePayerRowData & {
  onChangePayersPayedForPlayers?: (
    payerId: string,
    payedForIds: string[]
  ) => void;
  onChangeUnknownGuests?: (
    payerId: string,
    primaryChargeTypeId: string,
    unknownGuestCount: number
  ) => void;
  unknownGuestsOptionsForPayer?: {
    countAssignedToPayer: number;
    maximum: number;
    primaryChargeId: string | null;
    primaryChargeName: string;
    primaryChargePriceInCents: number;
  }[];
  isPaymentAssignment?: boolean;
  totalVariant?: "TOP" | "BOTTOM";
  displayName?: string | null;
  primaryChargeType?: "PER_SIGN_UP" | "PER_PERSON" | null;
  isPatronEventDisplay?: boolean;
  isOwner?: boolean;
  memberSignUpProxyAllowed?: boolean;
};

export const ChargePayerRow = ({
  payer,
  additionalChargeRows,
  shouldShowPayForDropdown,
  payForOptions,
  onChangePayersPayedForPlayers,
  onChangeUnknownGuests,
  totalVariant = "BOTTOM",
  primaryChargeType,
  unknownGuestsOptionsForPayer,
  isPatronEventDisplay = false,
  isPaymentAssignment = false,
  isOwner = false,
  memberSignUpProxyAllowed = false,
}: ChargePayerRowProps) => {
  const formatPrice = useFormatClubCurrency();
  const hasInitialUnknownGuests = !!unknownGuestsOptionsForPayer?.find(
    (item) => item.countAssignedToPayer > 0
  );

  const [
    shouldShowUnknownGuestPayForCounts,
    setShouldShowUnknownGuestPayForCounts,
  ] = useState(hasInitialUnknownGuests);

  const knownPlayerChargeRows = additionalChargeRows.filter(
    (row) => !(!row.id && row.type === "GUEST")
  );

  const totalPriceInCentsForKnownPlayers =
    primaryChargeType === "PER_SIGN_UP"
      ? payer.price
      : payer.price +
        knownPlayerChargeRows.reduce((acc, row) => acc + row.price, 0);

  const totalPriceInCentsForUnKnownPlayers =
    primaryChargeType === "PER_SIGN_UP"
      ? 0
      : unknownGuestsOptionsForPayer?.reduce((acc, cur) => {
          return acc + cur.primaryChargePriceInCents * cur.countAssignedToPayer;
        }, 0) ?? 0;

  const totalPriceInCents =
    totalPriceInCentsForKnownPlayers + totalPriceInCentsForUnKnownPlayers;

  /**
   * If we are not showing the dropdowns (are in display mode)
   * then we don't treat unknown guests separately in the UI,
   * but we still need to include their prices in the total.
   *
   * This is a bit of awkwardness of overeagerly moving this display component into the "design system" and
   * sharing it here. It can be decoupled later when someone has time.
   */
  const totalPriceInCentsForDisplayOnlyMode = shouldShowPayForDropdown
    ? 0
    : totalPriceInCents;

  /**
   * Again, if we are in 'display' mode, then we display all the rows, including unknown guests
   * but if we are in 'edit' mode we split unknown guests off into their own thing
   */
  const paidChargeRowsToDisplay = shouldShowPayForDropdown
    ? knownPlayerChargeRows
    : additionalChargeRows;

  const currentPayForValuesChargeRows = additionalChargeRows
    .map((row) => row.id)
    .filter((value) => value !== "");

  const hasUnknownGuestsAssigned = unknownGuestsOptionsForPayer?.some(
    (item) => item.countAssignedToPayer > 0
  );

  const currentPayForValues =
    shouldShowUnknownGuestPayForCounts || hasUnknownGuestsAssigned
      ? [...currentPayForValuesChargeRows, "unknown-guests"]
      : [...currentPayForValuesChargeRows];

  const handlePayForOptionChange = (values: string[]) => {
    //Which values are actual known players
    const knownPlayerValues = values.filter(
      (value) => value !== "unknown-guests"
    );
    const hasCheckedUnknownGuests = values.includes("unknown-guests");
    const previousHasUnknownGuests =
      currentPayForValues.includes("unknown-guests");

    // Call the callback if known players changed OR unknown guest checkbox changed
    if (
      knownPlayerChargeRows.length !== knownPlayerValues.length ||
      hasCheckedUnknownGuests !== previousHasUnknownGuests
    ) {
      onChangePayersPayedForPlayers?.(payer.id, values);
    }

    setShouldShowUnknownGuestPayForCounts(hasCheckedUnknownGuests);

    //If we didn't check unknown guests -- zero out our unknown guest counts
    if (!hasCheckedUnknownGuests) {
      unknownGuestsOptionsForPayer?.forEach((item) => {
        onChangeUnknownGuests?.(payer.id, item.primaryChargeId ?? "", 0);
      });
    }
  };

  const additionalChargeRowsCount = additionalChargeRows.length;

  const isTotalVariantTop = totalVariant === "TOP";

  const priceToDisplayInCents = shouldShowPayForDropdown
    ? totalPriceInCents
    : totalPriceInCentsForDisplayOnlyMode;

  const fullName = `${payer.firstName} ${payer.lastName}`;

  const payerAvatarType = payer.type === "MEMBER" ? "member" : "customer";

  return (
    <VStack gap={2}>
      <HStack px={isTotalVariantTop ? 0 : 6} py={2} alignItems="center">
        <Avatar
          profileImagePublicId={payer.profileImageId}
          firstName={payer.firstName}
          lastName={payer.lastName}
          type={payerAvatarType}
          size={isOwner && memberSignUpProxyAllowed ? "xs" : "sm"}
        />

        <VStack pl={4} flex={1}>
          <HStack gap={2} alignItems="center">
            <VStack>
              <Text maxFontSizeMultiplier={2} variant="mainBaseMedium">
                {fullName}
                {payer.isCurrentUser ? " " + t`(you)` : null}
              </Text>
              {payer.unknownRegistrantDisplayName && !payer.id ? (
                <Text
                  maxFontSizeMultiplier={2}
                  variant="mainTiny"
                  color="text-secondary"
                >
                  {payer.unknownRegistrantDisplayName}
                </Text>
              ) : null}
            </VStack>

            {isOwner && memberSignUpProxyAllowed ? (
              <Box
                height={18}
                borderRadius="1"
                width={46}
                backgroundColor="surface-primary"
                borderWidth={1}
                justifyContent="center"
                alignItems="center"
                borderColor="outline-default"
              >
                <Text
                  maxFontSizeMultiplier={2}
                  variant="mainMicro"
                >{t`OWNER`}</Text>
              </Box>
            ) : null}
          </HStack>
          {payer.detail ? (
            <Text
              maxFontSizeMultiplier={2}
              fontWeight={600}
              color="text-secondary"
            >
              {payer.detail}
            </Text>
          ) : null}

          {payer.displayName ? (
            <HStack gap={2}>
              <Text maxFontSizeMultiplier={2} variant="mainTiny">
                {payer.displayName}
              </Text>
              {payer.displayDescription ? (
                <Text
                  maxFontSizeMultiplier={2}
                  variant="mainTiny"
                  color="text-secondary"
                >
                  {payer.displayDescription}
                </Text>
              ) : null}
            </HStack>
          ) : null}
        </VStack>
        {isTotalVariantTop && priceToDisplayInCents > 0 ? (
          <VStack>
            <Text maxFontSizeMultiplier={2} variant="mainBaseMedium">
              {formatPrice(priceToDisplayInCents)}
            </Text>
            {!isPaymentAssignment ? (
              <Text
                maxFontSizeMultiplier={2}
                variant="mainTiny"
                color="text-secondary"
                textAlign="right"
              >
                {formatPrice(payer.price)}
              </Text>
            ) : null}
          </VStack>
        ) : priceToDisplayInCents ? (
          <VStack>
            <Text maxFontSizeMultiplier={2} variant="mainBaseMedium">
              {formatPrice(payer.price)}
            </Text>
            {!isPaymentAssignment ? (
              <Text
                maxFontSizeMultiplier={2}
                variant="mainTiny"
                color="text-secondary"
                textAlign="right"
              >
                {formatPrice(payer.price)}
              </Text>
            ) : null}
          </VStack>
        ) : null}
      </HStack>
      {shouldShowPayForDropdown ? (
        <HStack
          px={6}
          paddingBottom={additionalChargeRowsCount === 0 ? 2 : 0}
          gap={3}
          alignItems="center"
        >
          <Text
            maxFontSizeMultiplier={2}
            color="text-secondary"
          >{t`pays for`}</Text>
          <MultiSelectCheckbox
            options={payForOptions ?? []}
            values={currentPayForValues}
            onChange={(values) => {
              const setValues = values as string[];
              handlePayForOptionChange(setValues);
            }}
            testID={`pay-for-dropdown-${payer.id}`}
          />
        </HStack>
      ) : null}
      {paidChargeRowsToDisplay.map((payedForPerson, i) => {
        const paddingHorizontal = isTotalVariantTop
          ? memberSignUpProxyAllowed
            ? 3
            : 4
          : 6;

        const payedForPersonAvatarType =
          payedForPerson.type === "MEMBER"
            ? "member"
            : payedForPerson.type === "CUSTOMER"
            ? "customer"
            : "guest";
        return (
          <HStack
            px={paddingHorizontal}
            pr={!isPaymentAssignment ? 0 : isTotalVariantTop ? 4 : 6}
            key={payedForPerson.id + i}
            py={2}
            alignItems="center"
          >
            <Box mr={4}>
              <SubArrowIcon color={"main-primary-1000"} />
            </Box>

            <Avatar
              profileImagePublicId={payedForPerson.profileImageId}
              firstName={payedForPerson.firstName}
              lastName={payedForPerson.lastName}
              type={payedForPersonAvatarType}
              size="sm"
            />

            <VStack pl={4} flex={1}>
              <Text
                maxFontSizeMultiplier={2}
                variant="mainBaseMedium"
              >{`${payedForPerson.firstName} ${payedForPerson.lastName}`}</Text>

              {payedForPerson.detail ? (
                <Text
                  maxFontSizeMultiplier={2}
                  fontWeight={600}
                  color="text-secondary"
                >
                  {payedForPerson.detail}
                </Text>
              ) : null}
              {payedForPerson.displayName ? (
                <HStack gap={2}>
                  <Text maxFontSizeMultiplier={2} variant="mainTiny">
                    {payedForPerson.displayName}
                  </Text>
                  {payedForPerson.displayDescription ? (
                    <Text
                      maxFontSizeMultiplier={2}
                      variant="mainTiny"
                      color="text-secondary"
                    >
                      {payedForPerson.displayDescription}
                    </Text>
                  ) : null}
                </HStack>
              ) : null}
              {payedForPerson.unknownRegistrantDisplayName &&
              !payedForPerson.id ? (
                <Text
                  maxFontSizeMultiplier={2}
                  variant="mainTiny"
                  color="text-secondary"
                >
                  {payedForPerson.unknownRegistrantDisplayName}
                </Text>
              ) : null}
            </VStack>
            <VStack>
              {payedForPerson.price > 0 && !isTotalVariantTop ? (
                <Text
                  maxFontSizeMultiplier={2}
                  variant="mainBaseMedium"
                  textAlign="right"
                >
                  {formatPrice(payedForPerson.price)}
                </Text>
              ) : (
                <VStack height={4} />
              )}
              {!isPaymentAssignment ? (
                <Text
                  maxFontSizeMultiplier={2}
                  variant="mainTiny"
                  color="text-secondary"
                  textAlign="right"
                >
                  {formatPrice(payedForPerson.price)}
                </Text>
              ) : null}
            </VStack>
          </HStack>
        );
      })}
      {shouldShowUnknownGuestPayForCounts && unknownGuestsOptionsForPayer
        ? unknownGuestsOptionsForPayer.map((item) => {
            const dropdownOptions = new Array(item.maximum + 1)
              .fill("")
              .map((_, i) => ({ label: i.toString(), value: i.toString() }));
            return (
              <UnknownGuestChargeRow
                payerId={payer.id}
                chargeName={item.primaryChargeName}
                unknownGuestDropdownOptions={dropdownOptions}
                totalVariant={totalVariant}
                unknownGuestCount={item.countAssignedToPayer}
                onChangeUnknownGuests={(unknownGuestCount) => {
                  onChangeUnknownGuests?.(
                    payer.id,
                    item.primaryChargeId ?? "",
                    parseInt(unknownGuestCount)
                  );
                }}
                shouldShowPayForDropdown={shouldShowPayForDropdown}
                isPatronEventDisplay={isPatronEventDisplay}
              />
            );
          })
        : null}
      {totalVariant === "BOTTOM" ? (
        <HStack bg="grayscale-200" py={3} px={6}>
          <Text maxFontSizeMultiplier={2} flex={1} variant="mainBaseMedium">
            {payer.isCurrentUser
              ? t`Your Total`
              : t`${payer.firstName ?? ""} ${payer.lastName ?? ""}'s Total`}
          </Text>
          <Text maxFontSizeMultiplier={2} variant="mainBaseMedium">
            {formatPrice(totalPriceInCents)}
          </Text>
        </HStack>
      ) : null}
    </VStack>
  );
};
